/**
 * Vertical stepper for the pre-exam checks.
 *
 * Steps are strictly ordered and each must pass before the next opens — the
 * paper check is meaningless if the phone is not yet pointed at the desk, and
 * the room scan is meaningless before that. `Start` unlocks only when every
 * step is done.
 *
 * State is shown with an icon AND a word, never with colour alone.
 */
import { Check, Circle, Loader2, Lock } from "lucide-react";
import { cn } from "@/lib/cn";

export type StepStatus = "locked" | "active" | "done" | "waiting";

export interface StepDescriptor {
  id: string;
  title: string;
  description: string;
}

const STATUS_LABEL: Record<StepStatus, string> = {
  locked: "Locked",
  active: "In progress",
  waiting: "Waiting",
  done: "Done",
};

function StatusIcon({ status }: { status: StepStatus }) {
  switch (status) {
    case "done":
      return <Check className="size-3.5 text-answered" />;
    case "locked":
      return <Lock className="size-3.5 text-ink-faint" />;
    case "waiting":
      // No spin: docs/DESIGN.md bans pulsing/animated loading affordances.
      return <Loader2 className="size-3.5 text-review" />;
    case "active":
      return <Circle className="size-3.5 text-accent" />;
  }
}

export function SetupStepper({
  steps,
  statusOf,
  activeId,
  onSelect,
}: {
  steps: StepDescriptor[];
  statusOf: (id: string) => StepStatus;
  activeId: string;
  onSelect: (id: string) => void;
}) {
  return (
    <ol className="flex flex-col">
      {steps.map((step, i) => {
        const status = statusOf(step.id);
        const selected = step.id === activeId;
        const selectable = status !== "locked";

        return (
          <li key={step.id}>
            <button
              type="button"
              disabled={!selectable}
              onClick={() => onSelect(step.id)}
              aria-current={selected ? "step" : undefined}
              className={cn(
                "flex w-full items-start gap-2.5 border-b border-border px-3 py-2.5 text-left",
                "transition-colors last:border-b-0",
                selected ? "bg-accent-soft" : "bg-surface",
                selectable ? "hover:bg-surface-sunken" : "cursor-not-allowed",
                selected && "hover:bg-accent-soft",
              )}
            >
              <span
                className={cn(
                  "tabular mt-0.5 flex size-5 shrink-0 items-center justify-center",
                  "rounded-sm border text-2xs font-medium",
                  status === "done"
                    ? "border-answered/30 bg-answered-soft text-answered"
                    : status === "locked"
                      ? "border-border bg-neutral-soft text-ink-faint"
                      : "border-border-strong bg-surface text-ink",
                )}
              >
                {i + 1}
              </span>

              <span className="min-w-0 flex-1">
                <span className="flex items-center gap-1.5">
                  <span
                    className={cn(
                      "text-xs font-medium",
                      status === "locked" ? "text-ink-faint" : "text-ink",
                    )}
                  >
                    {step.title}
                  </span>
                  <StatusIcon status={status} />
                  <span className="sr-only">{STATUS_LABEL[status]}</span>
                </span>
                <span className="mt-0.5 block text-2xs text-ink-muted">
                  {step.description}
                </span>
              </span>
            </button>
          </li>
        );
      })}
    </ol>
  );
}
